import { ReactComponent as Simu } from 'assets/images/icons/dashboard/invoice-small.svg';
import { ReactComponent as Contrat } from 'assets/images/icons/dashboard/contratGroupe.svg';
import { ReactComponent as Contact } from 'assets/images/icons/dashboard/man.svg';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppFeature } from 'enums/appFeature';
import { useTranslation } from 'react-i18next';
import { AddIcon } from 'components/icons/Icons';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'components/domain/ProfilingForm/CheckboxButtonChoice';
import SelectBrokerBranches from './SelectBrokerBranches';
import { useNavigate } from 'react-router-dom';
import { NewGroupContractTypeModal } from 'back-office/features/group-contract-process/components/NewGroupContractTypeModal';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

type Props = {
  disabled?: boolean;
  className?: string;
};

type MenuAction = {
  key: AppFeature;
  label: string;
  icon: JSX.Element;
};

const MENU_OFFSET = {
  TOP: -95,
  LEFT: 80,
} as const;

const CreateMenu: React.FC<Props> = ({
  disabled = false,
  className = ''
}) => {
  const { openBlankDialog, closeDialog } = useDialog()
  const navigate = useNavigate()
  // Droits pilotés par la matrice centrale : affichage du menu de création + variante
  // courtier/admin des écrans contrat groupe.
  const canCreate = useRoleCan(Capability.CreateMenuAccess)
  const isBroker = useRoleCan(Capability.GroupContractBrokerMode)
  // Même garde que le bouton de la liste des contrats groupe : sans la brique
  // Souscription collective, l'entrée du menu est masquée.
  const canGroupContract = useRoleCan(Capability.GroupContractActions)

  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const actions: MenuAction[] = useMemo(() => [
    {
      key: AppFeature.CreateSimulation,
      label: 'SIDEBAR.CREATE_SIMULATION',
      icon: <Simu className="w-5 h-5 flex-shrink-0" />
    },
    ...(canGroupContract
      ? [{
          key: AppFeature.CreateCollectiveContract,
          label: 'SIDEBAR.CREATE_COLLECTIVE_CONTRACT',
          icon: <Contrat className="w-5 h-5 flex-shrink-0" />
        }]
      : []),
    {
      key: AppFeature.CreateContact,
      label: 'SIDEBAR.CREATE_CONTACT',
      icon: <Contact className="w-5 h-5 flex-shrink-0" />
    }
  ], [canGroupContract]);

  // Nettoyage des timeouts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Gestion des clics à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        closeMenu();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        closeMenu();
        buttonRef.current?.focus();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMenuOpen]);

  // Calcul de la position du menu
  const calculateMenuPosition = useCallback((): CSSProperties => {
    if (!buttonRef.current) {
      return { display: 'none' };
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuTop = buttonRect.top + buttonRect.height + MENU_OFFSET.TOP;
    const menuLeft = buttonRect.left + MENU_OFFSET.LEFT;

    return {
      position: 'absolute',
      top: `${menuTop}px`,
      left: `${menuLeft}px`,
      zIndex: 10,
    };
  }, []);

  // Fermeture du menu
  const closeMenu = useCallback(() => {
    setIsHovered(false);
    setIsMenuOpen(false);
  }, []);

  // Ouverture/fermeture du menu
  const toggleMenu = useCallback(() => {
    if (disabled) return;
    setIsMenuOpen(prev => !prev);
  }, [disabled]);

  // Gestion du hover avec debounce
  const handleMouseEnter = useCallback(() => {
    if (disabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    if (disabled) return;

    timeoutRef.current = setTimeout(() => {
      if (!isMenuOpen) {
        setIsHovered(false);
      }
    }, 100);
  }, [disabled, isMenuOpen]);

  // Gestion du clic sur une action
  const handleActionClick = useCallback((type: AppFeature) => {
    closeMenu();
    switch (type) {
      case AppFeature.CreateSimulation:
        openBlankDialog({
          size: Sizes['5xl']
        }, <SelectBrokerBranches />)
        break

      case AppFeature.CreateCollectiveContract:
        openBlankDialog(
          { size: Sizes['5xl'] },
          <NewGroupContractTypeModal
            onClose={closeDialog}
            mode={isBroker ? 'broker' : 'admin'}
          />,
        )
        break

      case AppFeature.CreateContact:
        navigate('/new-contact')
        break
    }
  }, [closeMenu, openBlankDialog, closeDialog, navigate, isBroker]);

  // Gestion des touches du clavier
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMenu();
    }
  }, [toggleMenu]);

  // Classes CSS du bouton
  const buttonClasses = useMemo(() => {
    const baseClasses = `
      absolute top-[80%] right-0 z-20
      h-10 bg-secondary text-white font-bold text-sm
      border-none outline-none
      flex items-center justify-center
      transition-all duration-300 ease-in-out
      mb-12 translate-x-5
      focus:ring-2 focus:ring-secondary focus:ring-opacity-50
      focus:outline-none
    `;

    const stateClasses = (isHovered || isMenuOpen) && !disabled
      ? 'w-24 rounded-3xl'
      : 'w-[40px] rounded-full';

    const interactionClasses = disabled
      ? 'opacity-50 cursor-not-allowed'
      : 'hover:w-24 hover:rounded-3xl cursor-pointer';

    return `${baseClasses} ${stateClasses} ${interactionClasses} ${className}`;
  }, [isHovered, isMenuOpen, disabled, className]);

  // Rendu du menu
  const renderMenu = useCallback(() => {
    if (!isMenuOpen) return null;

    return (
      <div
        style={calculateMenuPosition()}
        ref={menuRef}
        className="w-56 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
        role="menu"
        aria-label={t("COMMON.CREATE")}
      >
        {actions.map((action, index) => {
          const isLast = index === actions.length - 1;

          return (
            <button
              key={action.key}
              className={`
                w-full flex items-center px-4 py-2
                cursor-pointer transition-colors duration-200
                hover:bg-gray-50 active:bg-gray-100 focus:bg-gray-50
                focus:outline-none
                ${!isLast ? 'border-b border-gray-200' : ''}
              `}
              onClick={() => handleActionClick(action.key)}
              role="menuitem"
              tabIndex={0}
            >
              <div className="flex items-center justify-center w-8 h-8 mr-3 flex-shrink-0">
                {action.icon}
              </div>
              <span className="text-sm font-bold text-gray-800 select-none flex-1 text-left">
                {t(action.label)}
              </span>
            </button>
          );
        })}
      </div>
    );
  }, [isMenuOpen, actions, t, calculateMenuPosition, handleActionClick]);

  // Rôle sans droit de création (ex. Plateforme) : pas de bouton « Créer ».
  if (!canCreate) return null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={buttonClasses}
        aria-label={t("COMMON.CREATE")}
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
      >
        {(isHovered || isMenuOpen) && !disabled && (
          <span className="font-bold mr-3 whitespace-nowrap animate-fade-in">
            {t("COMMON.CREATE")}
          </span>
        )}
        <AddIcon
          className={`
            w-5 h-5 flex-shrink-0 transition-transform duration-300
            ${(isHovered || isMenuOpen) && !disabled ? 'translate-x-1.5' : ''}
          `}
        />
      </button>

      {renderMenu()}
    </>
  );
};

export default CreateMenu;
